'use client';

import {
  GRAPHICS_OPTIONS,
  IMAGE_COMPOSITION_OPTIONS,
  IMAGE_LIGHTING_OPTIONS,
  IMAGE_MOOD_OPTIONS,
  IMAGE_STYLE_OPTIONS,
  PRESET_PROMPTS,
} from '@/hooks/generator-constants';
import { Camera, ChevronDown, ChevronUp, Palette, Sparkles, Sun, Type } from 'lucide-react';
import React from 'react';

interface GeneratorImageEnhanceProps {
  activeDropdown: string | null;
  openSection: string | null;
  toggleSection: (section: string) => void;
  selectOption: (type: string, val: string, label: string) => void;
  setOpenSection: (section: string | null) => void;
}

export function GeneratorImageEnhance({
  activeDropdown,
  openSection,
  toggleSection,
  selectOption,
  setOpenSection,
}: GeneratorImageEnhanceProps) {
  if (activeDropdown !== 'settings') return null;

  return (
    <div className="select-options active sora-settings-dropdown !px-0">
      {/* Style / Preset */}
      <div className="border-white/10 border-b">
        <button
          type="button"
          className="flex w-full cursor-pointer items-center justify-between px-5 py-4 transition-colors hover:bg-white/5 active:bg-white/10"
          onClick={(e) => {
            e.stopPropagation();
            toggleSection('style');
          }}
        >
          <span className="flex items-center gap-1.5 font-bold text-[13px] text-gray-400 uppercase">
            <Palette className="h-3 w-3" /> Style
          </span>
          <div className="flex items-center text-gray-400">
            {openSection === 'style' ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
          </div>
        </button>
        {openSection === 'style' && (
          <div className="grid grid-cols-1 gap-1 bg-black/20 p-2">
            <div>
              {Object.entries(IMAGE_STYLE_OPTIONS).map(([val, label]) => (
                <button
                  key={val}
                  type="button"
                  className="option !text-[13px] !py-3 w-full px-4 text-left capitalize"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenSection(null);
                    selectOption('image-style', val, label);
                  }}
                >
                  <span className="capitalize">{val.replace(/-/g, ' ')}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Lighting */}
      <div className="border-white/10 border-b">
        <button
          type="button"
          className="flex w-full cursor-pointer items-center justify-between px-5 py-4 transition-colors hover:bg-white/5 active:bg-white/10"
          onClick={(e) => {
            e.stopPropagation();
            toggleSection('lighting');
          }}
        >
          <span className="flex items-center gap-1.5 font-bold text-[13px] text-gray-400 uppercase">
            <Sun className="h-3 w-3" /> Lighting
          </span>
          <div className="flex items-center text-gray-400">
            {openSection === 'lighting' ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
          </div>
        </button>
        {openSection === 'lighting' && (
          <div className="grid grid-cols-1 gap-1 bg-black/20 p-2">
            {Object.entries(IMAGE_LIGHTING_OPTIONS).map(([val, label]) => (
              <button
                key={val}
                type="button"
                className="option !text-[13px] !py-3 w-full px-4 text-left"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenSection(null);
                  selectOption('image-lighting', val, label);
                }}
              >
                <span className="capitalize">{val.replace(/-/g, ' ')}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Composition */}
      <div className="border-white/10 border-b">
        <button
          type="button"
          className="flex w-full cursor-pointer items-center justify-between px-5 py-4 transition-colors hover:bg-white/5 active:bg-white/10"
          onClick={(e) => {
            e.stopPropagation();
            toggleSection('composition');
          }}
        >
          <span className="flex items-center gap-1.5 font-bold text-[13px] text-gray-400 uppercase">
            <Camera className="h-3 w-3" /> Composition
          </span>
          <div className="flex items-center text-gray-400">
            {openSection === 'composition' ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
          </div>
        </button>
        {openSection === 'composition' && (
          <div className="grid grid-cols-1 gap-1 bg-black/20 p-2">
            {Object.entries(IMAGE_COMPOSITION_OPTIONS).map(([val, label]) => (
              <button
                key={val}
                type="button"
                className="option !text-[13px] !py-3 w-full px-4 text-left"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenSection(null);
                  selectOption('image-composition', val, label);
                }}
              >
                <span className="capitalize">{val.replace(/-/g, ' ')}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Mood */}
      <div className="border-white/10 border-b">
        <button
          type="button"
          className="flex w-full cursor-pointer items-center justify-between px-5 py-4 transition-colors hover:bg-white/5 active:bg-white/10"
          onClick={(e) => {
            e.stopPropagation();
            toggleSection('mood');
          }}
        >
          <span className="flex items-center gap-1.5 font-bold text-[13px] text-gray-400 uppercase">
            <Sparkles className="h-3 w-3" /> Mood
          </span>
          <div className="flex items-center text-gray-400">
            {openSection === 'mood' ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
          </div>
        </button>
        {openSection === 'mood' && (
          <div className="grid grid-cols-1 gap-1 bg-black/20 p-2">
            {Object.entries(IMAGE_MOOD_OPTIONS).map(([val, label]) => (
              <button
                key={val}
                type="button"
                className="option !text-[13px] !py-3 w-full px-4 text-left"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenSection(null);
                  selectOption('image-mood', val, label);
                }}
              >
                <span className="capitalize">{val.replace(/-/g, ' ')}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Graphics */}
      <div>
        <button
          type="button"
          className="flex w-full cursor-pointer items-center justify-between px-5 py-4 transition-colors hover:bg-white/5 active:bg-white/10"
          onClick={(e) => {
            e.stopPropagation();
            toggleSection('graphics');
          }}
        >
          <span className="flex items-center gap-1.5 font-bold text-[13px] text-gray-400 uppercase">
            <Type className="h-3 w-3" /> Graphics
          </span>
          <div className="flex items-center text-gray-400">
            {openSection === 'graphics' ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
          </div>
        </button>
        {openSection === 'graphics' && (
          <div className="grid grid-cols-1 gap-1 bg-black/20 p-2">
            {Object.entries(GRAPHICS_OPTIONS).map(([val, label]) => (
              <button
                key={val}
                type="button"
                className="option !text-[13px] !py-3 w-full px-4 text-left"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenSection(null);
                  selectOption('graphics', val, label);
                }}
              >
                <span className="capitalize">{val.replace(/-/g, ' ')}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
